import styles from "./MusicSuggestionsList.module.scss";
import { IMusicSuggestion } from "../../models/musicSuggestionModels";
import { MusicTrackItem } from "../MusicTrackItem";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";

interface IMusicSuggestionsListProps {
  suggestions: IMusicSuggestion[];
  isLoading: boolean;
  isAdminView?: boolean;
  hasActiveReservation?: boolean; // True if user has active reservation for this event
  onDelete: (suggestionId: number) => void;
  onUpvote: (suggestionId: number) => void;
  isDeleting?: boolean;
  isUpvoting?: boolean;
  audioPlayer: {
    play: (trackId: string, previewUrl: string) => void;
    stop: () => void;
    isTrackPlaying: (trackId: string) => boolean;
    currentTrackId: string | null;
    isPlaying: boolean;
  };
}

export const MusicSuggestionsList = ({
  suggestions,
  isLoading,
  isAdminView = false,
  hasActiveReservation = false,
  onDelete,
  onUpvote,
  audioPlayer,
}: IMusicSuggestionsListProps) => {
  // Show loading skeletons
  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.skeleton}>
            <div className={styles.skeletonImage} />
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLine} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Show empty state
  if (suggestions.length === 0) {
    return (
      <div className={styles.emptyState}>
        <i className="pi pi-music" />
        <p>Все още няма предложения</p>
        {!isAdminView && (
          <small>Бъдете първи, които предлагат музика за този клас!</small>
        )}
      </div>
    );
  }

  const handleDeleteWithConfirm = (suggestionId: number, trackId: string, trackName: string) => {
    showCustomConfirmDialog(
      `Сигурни ли сте че искате да изтриете предложението "<strong>${trackName}</strong>"?`,
      () => {
        // Stop audio if this track is currently playing
        if (audioPlayer.isTrackPlaying(trackId)) {
          audioPlayer.stop();
        }
        onDelete(suggestionId);
      },
      () => {},
      "warning",
      "Да",
      "Не"
    );
  };

  return (
    <div className={styles.suggestionsList}>
      {suggestions.map((suggestion) => {
        // Only show if track data is available
        if (!suggestion.track) return null;

        // Determine user interaction permissions
        const canInteract = isAdminView || hasActiveReservation;

        // Delete: Show for own suggestions (with reservation) OR for admins (always)
        const canDeleteThisSuggestion = canInteract && (suggestion.isOwnSuggestion || isAdminView);

        // Upvote button: Show for anyone who can interact (admin or has reservation)
        const shouldShowUpvoteButton = canInteract;

        // Upvote enabled: Only for regular users with reservation on others' suggestions
        const canActuallyUpvote = canInteract && !isAdminView && !suggestion.isOwnSuggestion;

        return (
          <MusicTrackItem
            key={suggestion.id}
            track={suggestion.track}
            variant="suggestion"
            suggestion={suggestion}
            onDelete={
              canDeleteThisSuggestion
                ? () => handleDeleteWithConfirm(suggestion.id, suggestion.track!.id, suggestion.track!.name)
                : undefined
            }
            onUpvote={shouldShowUpvoteButton ? () => onUpvote(suggestion.id) : undefined}
            disableUpvote={!canActuallyUpvote}
            isPlaying={audioPlayer.isTrackPlaying(suggestion.track.id)}
            onPlayToggle={
              suggestion.track.previewUrl
                ? () => audioPlayer.play(suggestion.track.id, suggestion.track.previewUrl!)
                : undefined
            }
          />
        );
      })}
    </div>
  );
};
